// src/stores/clientStore.js
import { create } from 'zustand';
import * as clientService from '../services/clientService';
import useUIStore from './uiStore';

const useClientStore = create((set, get) => ({
  // State
  clients: [],
  meta: {},
  totalClients: 0,
  currentClient: null,
  isLoading: false,
  criticalError: null,        // Only for client loading failures
  operationError: null,       // For non-critical operations
  
  // Fetch clients with pagination and filters
  assignUsersToClient: async (clientId, userIds) => {
    try {
      await clientService.assignUsersToClient(clientId, userIds);
      
      // Refresh client users after assignment
      await get().fetchClientUsers(clientId);
      
      useUIStore.getState().addNotification({
        message: 'Users assigned successfully',
        type: 'success'
      });
      
      return true;
    } catch (error) {
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to assign users to client',
        type: 'error'
      });
      throw error;
    }
  },
  fetchClients: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await clientService.getClients(params);
      set({
        clients: response.data.clients,
        totalClients: response.data.totalCount,
        meta: response.meta,
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch clients'
      });
     console.log('error', error);
    }
  },
  
  // Fetch a single client by ID
  fetchClient: async (clientId) => {
    set({ isLoading: true, criticalError: null, currentClient: null });
    try {
      const response = await clientService.getClient(clientId);
      set({
        currentClient: response.data.client,
        isLoading: false
      });
      return response.data.client;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch client details'
      });
     console.log('error', error);
    }
  },
  
  // Create a new client
  createClient: async (clientData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.createClient(clientData);
      // Add the new client to the list and increment total count
      set(state => ({
        clients: [response.data.client, ...state.clients],
        totalClients: state.totalClients + 1,
        isLoading: false
      }));
      return response.data.client;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to create client'
      });
     console.log('error', error);
    }
  },
  
  // Update an existing client
  updateClient: async (clientId, clientData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.updateClient(clientId, clientData);
      // Update the client in the list
      set(state => ({
        clients: state.clients.map(client => 
          client._id === clientId ? response.data.client : client
        ),
        currentClient: response.data.client,
        isLoading: false
      }));
      return response.data.client;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update client'
      });
     console.log('error', error);
    }
  },
  
  // Delete a client
  deleteClient: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      await clientService.deleteClient(clientId);
      // Remove the client from the list and decrement total count
      set(state => ({
        clients: state.clients.filter(client => client._id !== clientId),
        totalClients: state.totalClients - 1,
        isLoading: false
      }));
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to delete client'
      });
     console.log('error', error);
    }
  },
  
  // Upload client logo
  uploadLogo: async (clientId, logoFile) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.uploadClientLogo(clientId, logoFile);
      // Update client with new logo info
      set(state => ({
        clients: state.clients.map(client => 
          client._id === clientId 
            ? { ...client, logo: response.data.logo } 
            : client
        ),
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, logo: response.data.logo }
          : state.currentClient,
        isLoading: false
      }));
      return response.data.logo;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to upload client logo'
      });
     console.log('error', error);
    }
  },
  
  // Get client brands
  fetchClientBrands: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.getClientBrands(clientId);
      // Store brands in the current client object
      set(state => ({
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, brands: response.data.brands }
          : state.currentClient,
        isLoading: false
      }));
      return response.data.brands;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch client brands'
      });
     console.log('error', error);
    }
  },
  
  // Get client users
  fetchClientUsers: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.getClientUsers(clientId);
      set(state => ({
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, users: response.data.users }
          : state.currentClient,
        isLoading: false
      }));
      return response.data.users;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch client users'
      });
     console.log('error', error);
    }
  },
  removeUserFromClient: async (clientId, userId) => {
    set({ isLoading: true, error: null });
    try {
      await clientService.removeUserFromClient(clientId, userId);
      
      // Get fresh data instead of manipulating state
      const response = await clientService.getClientUsers(clientId);
      
      set(state => ({
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, users: response.data.users }
          : state.currentClient,
        isLoading: false
      }));
      
      return response.data.users;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to remove user from client'
      });
      throw error;
    }
  },
  // Get client campaigns
  fetchClientCampaigns: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.getClientCampaigns(clientId);
      set(state => ({
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, campaigns: response.data.campaigns }
          : state.currentClient,
        isLoading: false
      }));
      return response.data.campaigns;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch client campaigns'
      });
     console.log('error', error);
    }
  },
  
  // Get client analytics
  fetchClientAnalytics: async (clientId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await clientService.getClientAnalytics(clientId, params);
      set(state => ({
        currentClient: state.currentClient && state.currentClient._id === clientId
          ? { ...state.currentClient, analytics: response.data.analytics }
          : state.currentClient,
        isLoading: false
      }));
      return response.data.analytics;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch client analytics'
      });
     console.log('error', error);
    }
  },
  
  // Clear current client
  clearCurrentClient: () => {
    set({ currentClient: null });
  },
  
  // Clear critical error
  clearError: () => {
    set({ criticalError: null });
  },
  
  // Clear operation error
  clearOperationError: () => {
    set({ operationError: null });
  }
}));

export default useClientStore;